<template>
  <v-dialog v-model="dialog" max-width="900" scrollable @input="onDialogInput">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-h6">Administrar Libros</span>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="error" text dense>{{ errorMessage }}</v-alert>
        <v-progress-linear v-if="loading" indeterminate color="primary" />

        <v-tabs v-model="tab">
          <v-tab v-if="canManageBooks">Libros</v-tab>
          <v-tab v-if="canManageCodes">Códigos</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item v-if="canManageBooks">
            <div class="d-flex justify-end my-4">
              <v-btn small color="primary" dark @click="startBook">
                <v-icon small class="mr-1">mdi-plus</v-icon>
                Nuevo Libro
              </v-btn>
            </div>
            <v-form v-if="bookFormVisible" ref="bookForm" class="form-panel" @submit.prevent="saveBook">
              <div class="subtitle-2 mb-2">{{ editingBook ? 'Editar Libro' : 'Nuevo Libro' }}</div>
              <v-row dense>
                <v-col cols="12" md="5">
                  <v-text-field v-model="bookFormData.name" label="Nombre" outlined dense :rules="requiredRule" />
                </v-col>
                <v-col cols="12" md="5">
                  <v-select
                    v-model="bookFormData.code_id"
                    :items="activeCodes"
                    :item-text="codeText"
                    item-value="id"
                    label="Código"
                    outlined
                    dense
                    :rules="requiredRule"
                  />
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-switch v-model="bookFormData.is_active" label="Activo" dense />
                </v-col>
              </v-row>
              <div class="d-flex justify-end">
                <v-btn text small @click="cancelBook">Cancelar</v-btn>
                <v-btn small color="primary" dark type="submit" :loading="saving">Guardar</v-btn>
              </div>
            </v-form>

            <v-simple-table>
              <thead><tr><th>Nombre</th><th>Código</th><th>Folios</th><th>Estado</th><th class="text-right">Acciones</th></tr></thead>
              <tbody>
                <tr v-for="book in books" :key="book.id">
                  <td>{{ book.name }}</td>
                  <td>{{ book.code ? book.code.short_code : '-' }}</td>
                  <td>{{ book.folios_count || 0 }}</td>
                  <td>
                    <v-chip x-small :color="book.is_active ? 'green lighten-5' : 'grey lighten-2'">
                      {{ book.is_active ? 'Activo' : 'Inactivo' }}
                    </v-chip>
                  </td>
                  <td class="text-right">
                    <v-btn icon small title="Editar" @click="editBook(book)"><v-icon small>mdi-pencil</v-icon></v-btn>
                    <v-btn icon small title="Activar o desactivar" @click="toggleBook(book)">
                      <v-icon small>{{ book.is_active ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline' }}</v-icon>
                    </v-btn>
                    <v-btn icon small title="Eliminar" :disabled="book.folios_count > 0" @click="removeBook(book)">
                      <v-icon small color="error">mdi-delete-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr v-if="!books.length"><td colspan="5" class="text-center py-4">No hay Libros configurados.</td></tr>
              </tbody>
            </v-simple-table>
          </v-tab-item>

          <v-tab-item v-if="canManageCodes">
            <div class="d-flex justify-end my-4">
              <v-btn small color="primary" dark @click="startCode">
                <v-icon small class="mr-1">mdi-plus</v-icon>
                Nuevo Código
              </v-btn>
            </div>
            <v-form v-if="codeFormVisible" ref="codeForm" class="form-panel" @submit.prevent="saveCode">
              <div class="subtitle-2 mb-2">{{ editingCode ? 'Editar Código' : 'Nuevo Código' }}</div>
              <v-row dense>
                <v-col cols="12" md="3"><v-text-field v-model="codeFormData.short_code" label="Código corto" outlined dense :rules="requiredRule" /></v-col>
                <v-col cols="12" md="4"><v-text-field v-model="codeFormData.name" label="Nombre" outlined dense :rules="requiredRule" /></v-col>
                <v-col cols="12" md="5"><v-text-field v-model="codeFormData.description" label="Descripción" outlined dense /></v-col>
              </v-row>
              <div class="d-flex justify-end">
                <v-btn text small @click="cancelCode">Cancelar</v-btn>
                <v-btn small color="primary" dark type="submit" :loading="saving">Guardar</v-btn>
              </div>
            </v-form>

            <v-simple-table>
              <thead><tr><th>Código</th><th>Nombre</th><th>Asignado al proyecto</th><th class="text-right">Acciones</th></tr></thead>
              <tbody>
                <tr v-for="code in codes" :key="code.id">
                  <td class="font-weight-bold">{{ code.short_code }}</td>
                  <td>{{ code.name }}</td>
                  <td><v-switch :input-value="Boolean(code.project_is_active)" dense hide-details @change="setAssignment(code, $event)" /></td>
                  <td class="text-right"><v-btn icon small title="Editar" @click="editCode(code)"><v-icon small>mdi-pencil</v-icon></v-btn></td>
                </tr>
                <tr v-if="!codes.length"><td colspan="4" class="text-center py-4">No hay códigos asignados al proyecto.</td></tr>
              </tbody>
            </v-simple-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  deleteBook,
  listAdminBooks,
  listBookCodes,
  saveBook,
  saveBookCode,
  setBookCodeAssignment,
} from '@/helpers/api/communicationBooks'

const emptyBook = () => ({ name: '', code_id: null, is_active: true })
const emptyCode = () => ({ short_code: '', name: '', description: '' })

export default {
  name: 'CommunicationBooksAdminModal',
  props: {
    projectId: { type: [Number, String], required: true },
    canManageBooks: { type: Boolean, default: false },
    canManageCodes: { type: Boolean, default: false },
  },
  data: () => ({
    dialog: true,
    tab: 0,
    books: [],
    codes: [],
    loading: false,
    saving: false,
    error: null,
    bookFormVisible: false,
    codeFormVisible: false,
    editingBook: null,
    editingCode: null,
    bookFormData: emptyBook(),
    codeFormData: emptyCode(),
    requiredRule: [value => Boolean(value) || 'Campo requerido'],
  }),
  computed: {
    errorMessage() {
      return this.error?.data?.message || this.error?.message || 'No fue posible guardar los cambios.'
    },
    activeCodes() {
      return this.codes.filter(code => code.project_is_active && code.is_active)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        if (this.canManageBooks) {
          const books = await listAdminBooks(this.projectId)
          this.books = Array.isArray(books) ? books : []
        }
        if (this.canManageCodes) {
          const codes = await listBookCodes(this.projectId)
          this.codes = Array.isArray(codes)
            ? codes.map(code => Object.assign({}, code, {
              project_is_active: code.project_is_active === true || Number(code.project_is_active) === 1,
            }))
            : []
        }
      } catch (error) {
        this.error = error
      } finally {
        this.loading = false
      }
    },
    startBook() {
      this.editingBook = null
      this.bookFormData = emptyBook()
      this.bookFormVisible = true
    },
    editBook(book) {
      this.editingBook = book
      this.bookFormData = { name: book.name, code_id: book.code?.id || null, is_active: book.is_active }
      this.bookFormVisible = true
    },
    codeText(code) {
      return `${code.short_code} - ${code.name}`
    },
    cancelBook() {
      this.bookFormVisible = false
    },
    async saveBook() {
      if (!this.$refs.bookForm.validate()) return
      this.saving = true
      this.error = null
      try {
        await saveBook(Object.assign({ project_id: this.projectId }, this.bookFormData), this.editingBook?.id)
        this.cancelBook()
        await this.load()
      } catch (error) {
        this.error = error
      } finally {
        this.saving = false
      }
    },
    async toggleBook(book) {
      this.error = null
      try {
        await saveBook({ is_active: !book.is_active }, book.id)
        await this.load()
      } catch (error) {
        this.error = error
      }
    },
    async removeBook(book) {
      if (book.folios_count > 0) return
      this.error = null
      try {
        await deleteBook(book.id)
        await this.load()
      } catch (error) {
        this.error = error
      }
    },
    startCode() {
      this.editingCode = null
      this.codeFormData = emptyCode()
      this.codeFormVisible = true
    },
    editCode(code) {
      this.editingCode = code
      this.codeFormData = { short_code: code.short_code, name: code.name, description: code.description || '' }
      this.codeFormVisible = true
    },
    cancelCode() {
      this.codeFormVisible = false
    },
    async saveCode() {
      if (!this.$refs.codeForm.validate()) return
      this.saving = true
      this.error = null
      try {
        await saveBookCode(Object.assign({ project_id: this.projectId }, this.codeFormData), this.editingCode?.id)
        this.cancelCode()
        await this.load()
      } catch (error) {
        this.error = error
      } finally {
        this.saving = false
      }
    },
    async setAssignment(code, isActive) {
      this.error = null
      try {
        await setBookCodeAssignment(code.id, { project_id: this.projectId, is_active: isActive })
        await this.load()
      } catch (error) {
        this.error = error
      }
    },
    close() {
      this.dialog = false
      this.$emit('close')
    },
    onDialogInput(value) {
      if (!value) this.$emit('close')
    },
  },
}
</script>

<style scoped>
.form-panel {
  background: #f5f7fb;
  border: 1px solid #e0e5ef;
  padding: 14px;
  margin-bottom: 18px;
}
</style>
